import { Title, Text, FileButton, Stack } from "@mantine/core"
import { MIME_TYPES } from "@mantine/dropzone"
import { useDisclosure } from "@mantine/hooks"
import { IconDownload, IconUpload } from "@tabler/icons-react"
import { AreYouSureModal, Button, Card, EqualWidthGrid } from "@tfg/ui"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { showSuccessNotification } from "~/utils/showSuccessNotification"

type UploadButtonProps = {
  tooltip: string
  label: string
}

type DownloadTemplateButtonProps = {
  tooltip: string
  label: string
  href: string
}

type NotificationsProps = {
  erase: string
}

type AreYouSureModalProps = {
  areYouSureText: string
}

type ProductImportLegacyCardProps = {
  title: string
  description: string
  uploadButtonProps: UploadButtonProps
  notificationsProps: NotificationsProps
  areYouSureModalProps: AreYouSureModalProps
  uploadAction: ({ file }: { file: File }) => unknown
  downloadTemplateButtonProps?: DownloadTemplateButtonProps
}

export const ImportDocumentCard = ({
  title,
  description,
  uploadButtonProps,
  downloadTemplateButtonProps,
  notificationsProps,
  areYouSureModalProps,
  uploadAction,
}: ProductImportLegacyCardProps) => {
  const { t } = useTranslation(["dataTransfer", "common"])

  const [file, setFile] = useState<File | null>(null)

  const resetRef = useRef<() => void>(null)

  const [
    isAreYouSureModalOpen,
    { toggle: toggleAreYouSureModal, close: closeAreYouSureModal },
  ] = useDisclosure(false)

  const confirmFileUpload = (file: File | null): void => {
    setFile(file)
    toggleAreYouSureModal()
  }

  const discardUpload = (): void => {
    showSuccessNotification({
      message: notificationsProps.erase,
    })
  }

  const uploadFile = async () => {
    if (!file) return
    await uploadAction({
      file,
    })
  }

  const clearFile = (): void => {
    setFile(null)
    resetRef.current?.()
  }

  useEffect(() => {
    return () => {
      setFile(null)
    }
  }, [])

  return (
    <>
      <Card withBorder h="100%">
        <Stack gap="sm" h="100%">
          <Title order={3} fw={600}>
            {title}
          </Title>
          <Text>{description}</Text>
        </Stack>
        <EqualWidthGrid>
          {downloadTemplateButtonProps ? (
            <Button
              icon={<IconDownload size={16} />}
              component="a"
              href={downloadTemplateButtonProps.href}
              toolTip={downloadTemplateButtonProps.tooltip}
              variant="secondary"
              fullWidth
            >
              {downloadTemplateButtonProps.label}
            </Button>
          ) : null}
          <FileButton
            onChange={confirmFileUpload}
            resetRef={resetRef}
            accept={MIME_TYPES.csv}
          >
            {(props: { onClick: () => void }) => (
              <Button
                {...props}
                icon={<IconUpload size={16} />}
                toolTip={uploadButtonProps.tooltip}
                variant="primary"
                fullWidth
                onClick={() => {
                  clearFile()
                  props.onClick()
                }}
              >
                {uploadButtonProps.label}
              </Button>
            )}
          </FileButton>
        </EqualWidthGrid>
      </Card>
      <AreYouSureModal
        opened={isAreYouSureModalOpen}
        onClose={closeAreYouSureModal}
        onConfirm={uploadFile}
        onCancel={discardUpload}
        areYouSureText={areYouSureModalProps.areYouSureText}
        title={t("common:areYouSureModal.title")}
        confirmText={t("common:areYouSureModal.confirmText")}
        cancelText={t("common:areYouSureModal.cancelText")}
      />
    </>
  )
}
